import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, Edit, Trash2, Eye, Globe, MoreHorizontal, Building2, Filter, X, CheckSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useClients, useDeleteClient } from '@/hooks/use-clients';
import { useToast } from '@/hooks/use-toast';
import { DeleteAlertDialog } from '@/components/admin/DeleteAlertDialog';
import { Skeleton } from '@/components/ui/skeleton';

export default function AdminClientes() {
  const [searchTerm, setSearchTerm] = useState('');
  const { data: clients = [], isLoading, error, refetch } = useClients();

  // Debug logging
  console.log('AdminClientes render:', { clientsCount: clients.length, isLoading, error });

  const deleteClient = useDeleteClient();
  const { toast } = useToast();

  // Selection State
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Filter Logic
  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Selection Handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(filteredClients.map(c => c.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds(prev => [...prev, id]);
    } else {
      setSelectedIds(prev => prev.filter(item => item !== id));
    }
  };

  // Delete State
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteTitle, setDeleteTitle] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isBulkDelete, setIsBulkDelete] = useState(false);

  const confirmDelete = (id: string, name: string) => {
    setDeleteId(id);
    setDeleteTitle(name);
    setIsBulkDelete(false);
    setDeleteDialogOpen(true);
  };

  const confirmBulkDelete = () => {
    setDeleteTitle(`${selectedIds.length} clientes selecionados`);
    setIsBulkDelete(true);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      if (isBulkDelete) {
        // Bulk Delete
        await Promise.all(selectedIds.map(id => deleteClient.mutateAsync(id)));
        toast({
          title: "Clientes excluídos",
          description: `${selectedIds.length} clientes foram excluídos com sucesso.`,
        });
        setSelectedIds([]);
      } else if (deleteId) {
        // Single Delete
        await deleteClient.mutateAsync(deleteId);
        toast({
          title: "Cliente excluído",
          description: `O cliente "${deleteTitle}" foi excluído com sucesso.`,
        });
      }
      setDeleteDialogOpen(false);
    } catch (error) {
      toast({
        title: "Erro ao excluir",
        description: "Ocorreu um erro ao processar a exclusão.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-8 pb-10">
      {/* Premium Sticky Header */}
      <div className="sticky top-0 z-20 bg-background/80 backdrop-blur-md pb-4 pt-2 border-b mb-6 -mx-6 px-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Clientes</h1>
          <p className="text-muted-foreground hidden md:block">
            Gerencie seus clientes e parceiros estratégicos
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Atualizar Lista
          </Button>
          <Button asChild className="shadow-lg bg-blue-600 hover:bg-blue-700 transition-all">
            <Link to="/admin/clientes/novo">
              <Plus className="mr-2 h-4 w-4" />
              Novo Cliente
            </Link>
          </Button>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="border-0 shadow-lg overflow-hidden backdrop-blur-sm bg-card/50">
          <CardHeader className="border-b bg-muted/20 pb-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">

              {/* Selection Mode Actions */}
              {selectedIds.length > 0 ? (
                <div className="flex items-center justify-between w-full bg-blue-50/50 p-2 rounded-lg border border-blue-100 animate-in fade-in slide-in-from-top-2">
                  <div className="flex items-center gap-3 pl-2">
                    <Badge variant="secondary" className="bg-blue-100 text-blue-700 hover:bg-blue-100 border-blue-200">
                      {selectedIds.length} selecionado(s)
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedIds([])}
                      className="text-muted-foreground hover:text-foreground h-8 px-2"
                    >
                      <X className="h-3.5 w-3.5 mr-1" />
                      Cancelar
                    </Button>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={confirmBulkDelete}
                    className="h-8 shadow-sm"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-2" />
                    Excluir Seleção
                  </Button>
                </div>
              ) : (
                <>
                  {/* Search Bar */}
                  <div className="relative flex-1 max-w-md group">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground group-focus-within:text-blue-500 transition-colors" />
                    <Input
                      placeholder="Buscar por nome ou descrição..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 bg-background border-muted group-focus-within:border-blue-500 transition-colors shadow-sm"
                    />
                  </div>

                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Filter className="h-4 w-4" />
                    <span>{filteredClients.length} resultados</span>
                  </div>
                </>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  <TableHead className="w-[50px] pl-6">
                    <Checkbox
                      checked={filteredClients.length > 0 && selectedIds.length === filteredClients.length}
                      onCheckedChange={handleSelectAll}
                      aria-label="Selecionar todos"
                    />
                  </TableHead>
                  <TableHead className="w-[300px]">Cliente</TableHead>
                  <TableHead>Website</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead className="text-right pr-6">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <AnimatePresence>
                  {filteredClients.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center h-40 text-muted-foreground">
                        <div className="flex flex-col items-center justify-center">
                          <div className="h-12 w-12 bg-muted rounded-full flex items-center justify-center mb-3">
                            <Building2 className="h-6 w-6 text-muted-foreground/50" />
                          </div>
                          <p className="font-medium">Nenhum cliente encontrado</p>
                          <p className="text-sm text-muted-foreground/70">
                            {searchTerm ? 'Tente buscar com outros termos.' : 'Adicione seu primeiro cliente ou parceiro.'}
                          </p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredClients.map((client, index) => (
                      <motion.tr
                        key={client.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ delay: index * 0.05 }}
                        className={`group transition-colors border-b border-border/50 last:border-0 ${selectedIds.includes(client.id) ? 'bg-blue-50/30 hover:bg-blue-50/50' : 'hover:bg-muted/40'}`}
                        style={{ display: 'table-row' }}
                        layoutId={client.id}
                      >
                        <TableCell className="pl-6 py-4">
                          <Checkbox
                            checked={selectedIds.includes(client.id)}
                            onCheckedChange={(checked) => handleSelectOne(client.id, checked as boolean)}
                            aria-label={`Selecionar ${client.name}`}
                          />
                        </TableCell>
                        <TableCell className="py-4">
                          <div className="flex items-center gap-4">
                            <Avatar className="h-12 w-12 ring-2 ring-background shadow-sm">
                              <AvatarImage src={client.logo} alt={client.name} className="object-cover" />
                              <AvatarFallback className="bg-muted text-muted-foreground">
                                <Building2 className="h-5 w-5" />
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex flex-col">
                              <span className="font-semibold text-foreground group-hover:text-blue-600 transition-colors">{client.name}</span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {client.url ? (
                            <a
                              href={client.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-blue-500 hover:underline transition-colors w-fit"
                            >
                              <Globe className="h-3.5 w-3.5" />
                              {client.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                            </a>
                          ) : (
                            <span className="text-muted-foreground/50 text-sm italic">Sem site</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="text-muted-foreground text-sm truncate max-w-[300px] block" title={client.description}>
                            {client.description || '—'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right pr-6">
                          <div className="flex justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-transparent">
                                  <span className="sr-only">Abrir menu</span>
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-[160px]">
                                <DropdownMenuLabel>Ações</DropdownMenuLabel>
                                <DropdownMenuItem asChild>
                                  <Link to={`/admin/clientes/${client.id}/editar`} className="cursor-pointer">
                                    <Edit className="mr-2 h-4 w-4" />
                                    Editar
                                  </Link>
                                </DropdownMenuItem>
                                {client.url && (
                                  <DropdownMenuItem asChild>
                                    <a href={client.url} target="_blank" rel="noopener noreferrer" className="cursor-pointer">
                                      <Eye className="mr-2 h-4 w-4" />
                                      Visitar Site
                                    </a>
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => confirmDelete(client.id, client.name)}
                                  className="text-red-500 focus:text-red-500 cursor-pointer"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Excluir
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </motion.tr>
                    ))
                  )}
                </AnimatePresence>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </motion.div>

      <DeleteAlertDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={isBulkDelete ? "Excluir clientes selecionados?" : `Excluir "${deleteTitle}"?`}
        description={
          isBulkDelete
            ? `Esta ação excluirá ${selectedIds.length} clientes selecionados. Isso não pode ser desfeito.`
            : "Esta ação não pode ser desfeita. Isso excluirá permanentemente o cliente e sua logo."
        }
        onConfirm={handleDelete}
        isDeleting={deleteClient.isPending}
      />
    </div>
  );
}
